<template>
  <div class="invoice-apply-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('invoiceApply.title')" :subtitle="$t('invoiceApply.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="_invoiceApply">
          {{ $t('invoiceApply.apply') }}
        </el-button>
      </template>
    </ds-page-header>

    <div class="invoice-apply-layout">
      <!-- 左侧状态筛选 -->
      <div class="ds-panel ds-panel--tree">
        <ul class="state-list">
          <li v-for="(item, index) in states" :key="index" @click="swatchState(item)"
            :class="{ 'active-state': conditions.state === item.state }">
            {{ $t(`invoiceApply.states.${item.stateName}`) }}
          </li>
        </ul>
      </div>

      <div class="invoice-apply-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.invoiceCode" clearable class="w-full"
                :placeholder="$t('invoiceApply.invoiceCodePlaceholder')" @keyup.enter.native="_queryInvoiceApplyMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.invoiceType" clearable class="w-full"
                :placeholder="$t('invoiceApply.invoiceTypePlaceholder')">
                <el-option :label="$t('invoiceApply.page.selectInvoiceType')" value="" />
                <el-option :label="$t('invoiceApply.personal')" value="1001" />
                <el-option :label="$t('invoiceApply.company')" value="2002" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.ownerName" clearable class="w-full"
                :placeholder="$t('invoiceApply.ownerNamePlaceholder')" @keyup.enter.native="_queryInvoiceApplyMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.createUserName" clearable class="w-full"
                :placeholder="$t('invoiceApply.applicantPlaceholder')" @keyup.enter.native="_queryInvoiceApplyMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.applyTel" clearable class="w-full"
                :placeholder="$t('invoiceApply.phonePlaceholder')" @keyup.enter.native="_queryInvoiceApplyMethod" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryInvoiceApplyMethod">
              {{ $t('common.search') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="listInvoiceApplys">
            <template #left>
              <span class="ds-panel__title">{{ $t('invoiceApply.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="invoiceApplys" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('invoiceApply.page.empty')" @retry="listInvoiceApplys">
            <el-table-column prop="applyId" :label="$t('invoiceApply.table.id')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.applyId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="invoiceType" :label="$t('invoiceApply.table.invoiceType')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.invoiceType === '1001' ? $t('invoiceApply.personal') : $t('invoiceApply.company') }}
              </template>
            </el-table-column>
            <el-table-column prop="ownerName" :label="$t('invoiceApply.table.ownerName')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.ownerName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('invoiceApply.table.applicant')" align="center" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.createUserName }}({{ scope.row.applyTel }})
              </template>
            </el-table-column>
            <el-table-column prop="invoiceName" :label="$t('invoiceApply.table.invoiceName')" align="left" min-width="140"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.invoiceName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="invoiceNum" :label="$t('invoiceApply.table.taxNumber')" align="center" width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.invoiceNum || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="invoiceAddress" :label="$t('invoiceApply.table.addressPhone')" align="left" min-width="180"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.invoiceAddress || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="invoiceAmount" :label="$t('invoiceApply.table.amount')" align="right" width="130">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.invoiceAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="invoiceCode" :label="$t('invoiceApply.table.invoiceCode')" align="center" width="150">
              <template slot-scope="scope">
                {{ scope.row.invoiceCode || $t('invoiceApply.notUploaded') }}
              </template>
            </el-table-column>
            <el-table-column prop="stateName" :label="$t('invoiceApply.table.status')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveStateType(scope.row.state)" :label="scope.row.stateName" />
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('invoiceApply.table.applyTime')" align="center" width="170">
              <template slot-scope="scope">
                {{ formatDateTimeSec(scope.row.createTime) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="240">
              <template slot-scope="scope">
                <el-button v-if="scope.row.state === 'W'" type="text" size="small"
                  @click="_openInvoiceAuditModel(scope.row)">
                  {{ $t('common.audit') }}
                </el-button>
                <template v-else>
                  <el-button v-if="hasPlugin('invoice') && scope.row.state !== 'W'" type="text" size="small"
                    @click="_toOpenInvoicePlugin(scope.row)">
                    {{ $t('invoiceApply.openInvoice') }}
                  </el-button>
                  <div v-else>
                    <el-button v-if="['U', 'G', 'C'].includes(scope.row.state)" type="text" size="small"
                      @click="_openUploadInvoicePhoto(scope.row)">
                      {{ scope.row.state === 'U' ? $t('invoiceApply.uploadInvoice') : $t('invoiceApply.reUpload') }}
                    </el-button>
                    <el-button v-if="['U', 'G', 'C'].includes(scope.row.state)" type="text" size="small"
                      @click="_openUserGetInvoice(scope.row)">
                      {{ scope.row.state === 'G' ? $t('invoiceApply.verify') : $t('invoiceApply.register') }}
                    </el-button>
                  </div>
                </template>
                <el-button type="text" size="small" class="ds-btn-danger"
                  @click="_openDeleteInvoiceApplyModel(scope.row)">
                  {{ $t('common.delete') }}
                </el-button>
                <el-button type="text" size="small" @click="_openInvoiceApplyDetail(scope.row)">
                  {{ $t('common.detail') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </div>
    </div>

    <delete-invoice-apply ref="deleteDialog" @success="listInvoiceApplys" />
    <upload-invoice-photo ref="uploadPhotoDialog" @success="listInvoiceApplys" />
    <wirte-invoice-event ref="writeEventDialog" @success="listInvoiceApplys" />
    <audit-dialog ref="auditDialog" @success="notifyAuditInfo" />
  </div>
</template>

<script>
import { listInvoiceApply, auditInvoiceApply } from '@/api/fee/invoiceApplyApi'
import { getCommunityId } from '@/api/community/communityApi'
import DeleteInvoiceApply from '@/components/fee/deleteInvoiceApply'
import UploadInvoicePhoto from '@/components/fee/uploadInvoicePhoto'
import WirteInvoiceEvent from '@/components/fee/wirteInvoiceEvent'
import AuditDialog from '@/components/fee/audit'
import { jumpToPlugin } from '@/api/user/menuApi'
import { getUserName } from '@/api/user/userApi'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'InvoiceApplyList',
  components: {
    DeleteInvoiceApply,
    UploadInvoicePhoto,
    WirteInvoiceEvent,
    AuditDialog,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      states: [
        { stateName: 'all', state: '' },
        { stateName: 'pendingReview', state: 'W' },
        { stateName: 'pendingUpload', state: 'U' },
        { stateName: 'reviewFailed', state: 'F' },
        { stateName: 'pendingReceive', state: 'G' },
        { stateName: 'received', state: 'C' }
      ],
      conditions: {
        invoiceCode: '',
        invoiceType: '',
        ownerName: '',
        applyTel: '',
        createUserName: '',
        state: ''
      },
      invoiceApplys: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      auditData: {}
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.listInvoiceApplys()
  },
  methods: {
    async listInvoiceApplys() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.conditions,
          page: this.page.current,
          row: this.page.size,
          communityId: this.communityId
        }
        const res = await listInvoiceApply(params)
        this.invoiceApplys = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('invoiceApply.page.loadFailed')
        this.$message.error(this.$t('common.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(size) {
      this.page.size = size
      this.listInvoiceApplys()
    },
    handleCurrentChange(current) {
      this.page.current = current
      this.listInvoiceApplys()
    },
    swatchState(item) {
      this.conditions.state = item.state
      this.page.current = 1
      this.listInvoiceApplys()
    },
    _queryInvoiceApplyMethod() {
      this.page.current = 1
      this.listInvoiceApplys()
    },
    _invoiceApply() {
      this.$router.push('/views/fee/ownerApplyInvoice')
    },
    _openInvoiceApplyDetail(row) {
      this.$router.push(`/views/fee/invoiceApplyDetail?applyId=${row.applyId}`)
    },
    _openInvoiceAuditModel(row) {
      this.auditData = { ...row }
      this.$refs.auditDialog.open()
    },
    async notifyAuditInfo(auditInfo) {
      try {
        const params = {
          ...this.auditData,
          state: auditInfo.state,
          remark: auditInfo.remark,
          communityId: this.communityId
        }
        await auditInvoiceApply(params)
        this.$message.success(this.$t('common.operationSuccess'))
        this.listInvoiceApplys()
      } catch (error) {
        this.$message.error(error.message)
      }
    },
    _openUploadInvoicePhoto(row) {
      this.$refs.uploadPhotoDialog.open(row)
    },
    _openUserGetInvoice(row) {
      this.$refs.writeEventDialog.open(row)
    },
    _toOpenInvoicePlugin(row) {
      jumpToPlugin(`/#/pages/index/plugin?pluginType=invoice&orderNo=&orderNo=${row.applyId}&staffName=${getUserName()}&communityId=${this.communityId}`)
    },
    _openDeleteInvoiceApplyModel(row) {
      this.$refs.deleteDialog.open(row)
    },
    resolveStateType(state) {
      if (state === 'C') return 'success'
      if (state === 'W') return 'warning'
      if (state === 'F') return 'danger'
      return 'muted'
    },
    hasPlugin(pluginType) {
      const sysInfoStr = window.localStorage.getItem('java110SystemInfo')
      if (!sysInfoStr) {
        return false
      }
      let sysInfo
      try {
        sysInfo = JSON.parse(sysInfoStr)
      } catch (error) {
        return false
      }
      if (!sysInfo || !Object.prototype.hasOwnProperty.call(sysInfo, 'plugins')) {
        return false
      }
      const plugins = sysInfo.plugins
      let hasPlugin = false
      plugins.forEach(p => {
        if (pluginType === p.pluginType) {
          hasPlugin = true
        }
      })
      return hasPlugin
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.invoice-apply-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧状态筛选 + 右侧主内容 */
.invoice-apply-layout {
  display: flex;
  gap: var(--ds-spacing-md);
  align-items: flex-start;
}

.invoice-apply-main {
  flex: 1 1 auto;
  min-width: 0;
}

/* 状态筛选面板 */
.ds-panel--tree {
  width: 180px;
  flex-shrink: 0;
}

.state-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    padding: 10px;
    text-align: center;
    cursor: pointer;
    border-radius: var(--ds-radius-sm);
    margin-bottom: 5px;
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    transition: background-color var(--ds-transition-fast), color var(--ds-transition-fast);

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &.active-state {
      background-color: var(--ds-primary);
      color: #fff;
    }
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
